import { z } from 'zod';

/**
 * The SimpleACS host's own resources — CPU, memory, disk — for the superadmin's
 * dashboard card.
 *
 * This is host information, not tenant information: there is one server behind
 * every organization on it, so the numbers here belong to `infra.manage` and to
 * nobody else.
 *
 * Every field that a given host cannot honestly answer is nullable and carries
 * a `note` saying why, because the alternative — a plausible-looking number
 * derived from something else — is how a dashboard teaches an operator to stop
 * trusting it. Production is Linux and development is Windows, and the two
 * kernels genuinely know different things about themselves.
 */

/**
 * Where the "free memory" figure came from.
 *
 * `meminfo` means `MemAvailable` out of `/proc/meminfo`: the kernel's own
 * estimate of what a new workload could claim without swapping, page cache
 * included. `freemem` means `os.freemem()`, which on Linux counts cache and
 * buffers as *used* and therefore badly understates what is really free — a
 * healthy Linux box reports nearly all of its RAM "in use" that way.
 */
export const memorySourceSchema = z.enum(['meminfo', 'freemem']);
export type MemorySource = z.infer<typeof memorySourceSchema>;

/** One mounted filesystem, in the `df` sense. */
export const serverDiskSchema = z.object({
  /** What lives here, in the operator's terms — "Database", "File store". */
  label: z.string(),
  /** The directory that was measured, not the mount point (we cannot name it portably). */
  path: z.string(),
  totalBytes: z.number().int(),
  /** Free to an unprivileged writer — `bavail`, so root's reserve is excluded. */
  freeBytes: z.number().int(),
  usedBytes: z.number().int(),
});
export type ServerDisk = z.infer<typeof serverDiskSchema>;

export const serverMetricsSchema = z.object({
  /** When the sample was taken, so a stalled card is visibly stalled. */
  at: z.number().int(),
  host: z.object({
    /** `process.platform` verbatim: 'linux' in production, 'win32' in development. */
    platform: z.string(),
    release: z.string(),
    hostname: z.string(),
    uptimeSeconds: z.number().int(),
    /** The API process's own uptime, which is the one that resets on a deploy. */
    processUptimeSeconds: z.number().int(),
    node: z.string(),
  }),
  cpu: z.object({
    model: z.string(),
    cores: z.number().int(),
    /**
     * Busy percentage over `sampleMs`, never from a single reading:
     * `os.cpus()` counts jiffies since boot, so one reading only ever yields
     * the average since the machine came up. Null when the two samples were
     * too close together to have accumulated a tick.
     */
    usagePercent: z.number().nullable(),
    /** The width of the window the percentage was measured over. */
    sampleMs: z.number().int().nullable(),
    /** 1/5/15-minute load average. Null on Windows, which has no such thing. */
    load: z.tuple([z.number(), z.number(), z.number()]).nullable(),
    /** `load[0] / cores` — the figure that actually says "saturated" or "not". */
    loadPerCore: z.number().nullable(),
    note: z.string().nullable(),
  }),
  memory: z.object({
    totalBytes: z.number().int(),
    availableBytes: z.number().int(),
    usedBytes: z.number().int(),
    source: memorySourceSchema,
    note: z.string().nullable(),
    /** Null where swap is not reported (no `/proc/meminfo`) or not configured. */
    swap: z.object({ totalBytes: z.number().int(), usedBytes: z.number().int() }).nullable(),
  }),
  /** Deduplicated: two paths on one filesystem are reported once. */
  disks: z.array(serverDiskSchema),
  diskNote: z.string().nullable(),
  /**
   * The database, whichever engine is behind it.
   *
   * `bytes` is the size on disk: for SQLite the file plus its WAL and shm
   * sidecars, for PostgreSQL what `pg_database_size()` reports for the database
   * this platform connects to. `postgres` is null on a SQLite install and on a
   * server that could not be asked, so the card renders the engine it actually
   * has rather than a block of dashes.
   */
  database: z.object({
    path: z.string(),
    bytes: z.number().int().nullable(),
    note: z.string().nullable(),
    postgres: z
      .object({
        /** e.g. "18.6" — the server's, not the client library's. */
        version: z.string(),
        /** Backends connected against the server's limit; the pair is the useful thing. */
        connectionsUsed: z.number().int(),
        connectionsMax: z.number().int(),
        /**
         * Percentage of block reads served from shared buffers. Healthy is high
         * — under about 95 on a working set this small means something is
         * scanning what it should be seeking.
         */
        cacheHitPercent: z.number().nullable(),
        /**
         * Age in seconds of the longest-running transaction, or null when none
         * is open. This is the number that explains a database that has stopped
         * reclaiming space: one forgotten transaction holds the horizon back
         * and every autovacuum after it does nothing.
         */
        oldestTransactionSec: z.number().nullable(),
        /** Rows the fleet's own tables hold, for scale beside the byte count. */
        liveRows: z.number().int().nullable(),
      })
      .nullable(),
  }),
  fileStore: z.object({
    /** 'local', 's3' or 'bunny' — only 'local' consumes this server's disk. */
    kind: z.string(),
    path: z.string().nullable(),
    bytes: z.number().int().nullable(),
    /** True when the walk hit its file/time cap and the total is a floor, not a total. */
    truncated: z.boolean(),
    note: z.string().nullable(),
  }),
});
export type ServerMetrics = z.infer<typeof serverMetricsSchema>;
